import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import 'react-phone-number-input/style.css';
import { Search, Send } from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import AvailableCreditsCallout from '../components/AvailableCreditsCallout';
import SellCreditsCashToPlayerTrigger from '../components/credits/SellCreditsCashToPlayerTrigger';
import { dispatchShopCreditBalanceRefresh } from '../constants';
import {
  fetchDashboard,
  lookupPlayerByPhone,
  registerPlayerInitiate,
  verifyPlayerRegistration,
  type ShopPlayerLookupStatus,
} from '../services/api';
import { isPlausibleInternationalPhone } from '../utils/phone';
import {
  parseLowBalanceWarningSettings,
  type LowBalanceWarningSettings,
} from '../utils/lowBalanceWarning';

export default function TransferToPlayerPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [lookupStatus, setLookupStatus] = useState<ShopPlayerLookupStatus | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [otpStep, setOtpStep] = useState(false);
  const [transferId, setTransferId] = useState('');
  const [otp, setOtp] = useState('');
  const [otpKind, setOtpKind] = useState<'create' | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [shopBalance, setShopBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [lowBalanceWarning, setLowBalanceWarning] = useState<LowBalanceWarningSettings>({
    enabled: true,
    effectiveThresholdUsd: null,
  });

  const loadShopBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data != null) {
        const raw = res.data.credit_balance;
        const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
        setShopBalance(Number.isFinite(n) ? n : null);
        setLowBalanceWarning(parseLowBalanceWarningSettings(res.data));
      } else {
        setShopBalance(null);
        setLowBalanceWarning({ enabled: true, effectiveThresholdUsd: null });
      }
    } catch {
      setShopBalance(null);
      setLowBalanceWarning({ enabled: true, effectiveThresholdUsd: null });
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadShopBalance();
  }, [loadShopBalance]);

  const canAddCredits = lookupStatus === 'in_shop' && playerId != null;
  const resetOtp = () => {
    setOtpStep(false);
    setTransferId('');
    setOtp('');
    setOtpKind(null);
  };

  const handlePhoneChange = (v: string | undefined) => {
    setPhone(v ?? '');
    setLookupStatus(null);
    setPlayerId(null);
    resetOtp();
  };

  const runLookupForPhone = useCallback(async (phoneValue: string) => {
    const trimmed = phoneValue.trim();
    if (!isPlausibleInternationalPhone(trimmed)) return;
    setLookupLoading(true);
    setOtpStep(false);
    setTransferId('');
    setOtp('');
    setOtpKind(null);
    try {
      const res = await lookupPlayerByPhone(trimmed);
      if (!res.success || !res.data) {
        toast.error(res.message || 'Lookup failed');
        return;
      }
      setLookupStatus(res.data.status);
      setPlayerId(res.data.playerId ?? null);
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data && typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : 'Lookup failed';
      toast.error(msg);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  const onLookup = async () => {
    if (!isPlausibleInternationalPhone(phone)) {
      toast.error('Enter a phone number with country code (e.g. +1, +84)');
      return;
    }
    await runLookupForPhone(phone);
  };

  /** Opened from Players row: pre-fill phone and run lookup */
  useEffect(() => {
    const pre = (location.state as { prefillPhone?: string } | null)?.prefillPhone?.trim();
    if (!pre || !isPlausibleInternationalPhone(pre)) return;
    setPhone(pre);
    navigate('.', { replace: true, state: {} });
    void runLookupForPhone(pre);
  }, [location.state, navigate, runLookupForPhone]);

  const onRegisterInitiate = async () => {
    if (!isPlausibleInternationalPhone(phone)) {
      toast.error('Enter a phone number with country code (e.g. +1, +84)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await registerPlayerInitiate(phone);
      if (!res.success || !res.data?.registration_id) {
        toast.error(res.message || 'Could not start OTP');
        return;
      }
      setTransferId(res.data.registration_id);
      setOtpKind('create');
      setOtpStep(true);
      toast.success('OTP sent to this phone by SMS');
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data && typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : 'Could not start OTP';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (!transferId || !otp.trim()) {
      toast.error('Enter the OTP');
      return;
    }
    setSubmitting(true);
    try {
      const res = await verifyPlayerRegistration(transferId, otp.trim());
      if (!res.success) {
        toast.error(res.message || 'Verification failed');
        return;
      }
      toast.success('Player created and linked to your Shop');
      resetOtp();
      const prevPid = playerId;
      const again = await lookupPlayerByPhone(phone);
      if (again.success && again.data) {
        setLookupStatus(again.data.status);
        setPlayerId(again.data.playerId ?? prevPid ?? null);
      } else {
        setLookupStatus('in_shop');
        setPlayerId(prevPid ?? null);
      }
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data && typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : 'Verification failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onDepositSuccess = useCallback(() => {
    void loadShopBalance();
    dispatchShopCreditBalanceRefresh();
  }, [loadShopBalance]);

  const statusHelp = (() => {
    switch (lookupStatus) {
      case 'not_found':
        return 'No account for this phone. Send an OTP to create a Player and link them to your Shop (no credits yet).';
      case 'unassigned':
        return 'This Player already exists, so we couldn’t add/link them to your Shop.';
      case 'other_shop':
        return 'This Player doesn’t belong to your Shop, so you can’t create them.';
      case 'in_shop':
        return 'This Player is on your Shop. Use Deposit credits to record cash and transfer credits.';
      default:
        return 'Enter a phone number and tap Look up.';
    }
  })();

  return (
    <AppLayout contentFullWidth>
      <div className="mb-6 flex items-start gap-3">
        <div className="rounded-lg bg-emerald-100 p-2 text-emerald-800">
          <Send className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-stone-900">Add Player credits</h1>
          <p className="mt-1 text-sm text-stone-500">
            Look up by phone first. You can only create a new Player if they don’t already exist. Adding credits is only available for Players in your Shop.
          </p>
        </div>
      </div>

      <div className="max-w-2xl space-y-6">
        <div className="rounded-xl border border-emerald-100 bg-white p-6 shadow-sm">
          <AvailableCreditsCallout
            balance={shopBalance}
            loading={balanceLoading}
            lowBalanceWarning={lowBalanceWarning}
            className="mb-4"
          />
          <label htmlFor="credit-phone" className="block text-sm font-medium text-stone-700">
            Player phone
          </label>
          <div className="transfer-phone-field mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <PhoneInput
                id="credit-phone"
                international
                defaultCountry="US"
                countryCallingCodeEditable={false}
                labels={en}
                placeholder="Enter phone number"
                value={phone || undefined}
                onChange={handlePhoneChange}
                className="PhoneInput"
                autoComplete="tel"
              />
            </div>
            <button
              type="button"
              disabled={lookupLoading}
              onClick={() => void onLookup()}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-stone-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-60"
            >
              <Search className="h-4 w-4" />
              {lookupLoading ? 'Looking up…' : 'Look up'}
            </button>
          </div>
          <p className="mt-3 text-sm text-stone-600">{statusHelp}</p>

          {lookupStatus != null && lookupStatus !== 'in_shop' && !otpStep && (
            <div className="mt-4 border-t border-stone-100 pt-4">
              {lookupStatus === 'not_found' && (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void onRegisterInitiate()}
                  className="w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  Send OTP to create &amp; link
                </button>
              )}
              {(lookupStatus === 'unassigned' || lookupStatus === 'other_shop') && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {lookupStatus === 'other_shop'
                    ? 'This Player doesn’t belong to your Shop, so you can’t create them'
                    : 'This Player is already in the Shop, so we couldn’t add them again.'}
                </div>
              )}
            </div>
          )}
        </div>

        {otpStep && (
          <div className="rounded-xl border border-emerald-100 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-stone-900">Enter OTP</h2>
            <p className="mt-1 text-sm text-stone-500">
              Code was sent to the phone number you entered.
            </p>
            <form onSubmit={onVerifyOtp} className="mt-4 space-y-4">
              <div>
                <label htmlFor="credit-otp" className="block text-sm font-medium text-stone-700">
                  6-digit OTP
                </label>
                <input
                  id="credit-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-1.5 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm tracking-widest focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                  maxLength={6}
                  required
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={resetOtp}
                  className="flex-1 rounded-lg border border-stone-200 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {submitting ? 'Verifying…' : 'Verify & link'}
                </button>
              </div>
            </form>
          </div>
        )}

        {canAddCredits && !otpStep && playerId && (
          <div className="rounded-xl border border-emerald-100 bg-white p-6 shadow-sm space-y-4">
            <AvailableCreditsCallout
              balance={shopBalance}
              loading={balanceLoading}
              lowBalanceWarning={lowBalanceWarning}
            />
            <SellCreditsCashToPlayerTrigger
              playerId={playerId}
              playerName={phone}
              creditBalance={shopBalance}
              creditBalanceLoading={balanceLoading}
              lowBalanceWarning={lowBalanceWarning}
              onSuccess={onDepositSuccess}
              buttonText="Deposit credits"
              className="w-full justify-center"
            />
          </div>
        )}
      </div>
    </AppLayout>
  );
}
